<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import axios from 'axios'
import { useUserStore } from '@/stores/user'
import { useToast } from 'primevue/usetoast'
import DashboardPaymentsSummaryTab from '@/pages/dashboard/DashboardPaymentsSummaryTab.vue'
import Tabs from 'primevue/tabs'
import TabList from 'primevue/tablist'
import Tab from 'primevue/tab'
import TabPanels from 'primevue/tabpanels'
import TabPanel from 'primevue/tabpanel'

/** Вкладка «Заказы» скрыта, код сохранён для быстрого возврата. */
const SHOW_ORDERS_TAB = false
const activeDashboardTab = ref(SHOW_ORDERS_TAB ? 'orders' : 'summary')

const userStore = useUserStore()
const toast = useToast()
const apiBase = 'https://ncht.bz'

const loading = ref(false)
const metricsLoading = ref(false)
const exportLoading = ref(false)

const rows = ref([])
const pageSize = ref(100)
const page = ref(1)
const total = ref(0)
const sortField = ref('created_at')
const sortOrder = ref(-1)
const expandedRows = ref({})
const dealPaymentsByDealNumber = ref({})
const dealPaymentsLoadingByDealNumber = ref({})
const dealPaymentsErrorByDealNumber = ref({})

/** Доли от price_amount для скрытых колонок 0–12 (после «Остаток суммы…»). */
const PERIOD_PERCENTS = Object.freeze([23, 7, 7, 7, 7, 7, 7, 7, 7, 7, 7, 6, 1])
const showPeriodColumns = ref(false)

const transactionTypeOptions = ref([])
const statusOptions = ref([])
const paymentTypeSourceToTarget = {
  Robokassa: 'Робокасса',
  PayAnyWay: 'Крипта',
  Card: 'PayToday',
  WalletOne: 'Ванта',
  Cashless: 'Безналичный расчёт',
  CloudPaymentsKz: 'РесурсРазвития',
  'T-Bank': 'ТБанк рассрочка',
  Геткурс: 'GetCourse PAY',
  'Счет на оплату': 'Безналичный расчёт',
  'T-Банк кредит': 'ТБанк рассрочка',
  'Internal balance': 'Другое',
  Cash: 'Наличные д/с',
  Other: 'Другое'
}
const paymentTypeTargetToSources = Object.entries(paymentTypeSourceToTarget).reduce((acc, [source, target]) => {
  if (!acc[target]) acc[target] = []
  acc[target].push(source)
  return acc
}, {})
const productTitleMap = {
  '[9 сезон] продление из 8 в 9 сезон': [
    'за оказание образовательных услуг по оферте №14.1 от 17/10/2025',
    'за оказание образовательных услуг по оферте №14.2 от 13/01/2026',
    'за оказание образовательных услуг по оферте №14.3 от 05/03/2026',
    'за оказание образовательных услуг по оферте №14.4 от 01/04/2026'
  ],
  '[9 сезон] новый участник': [
    'за оказание образовательных услуг по оферте №16.1 от 21/10/2025',
    'за оказание образовательных услуг по оферте №16.2 от 13/01/2026',
    'за оказание образовательных услуг по оферте №16.3 от 26/02/2026',
    'за оказание образовательных услуг по оферте №16.4 от 06/03/2026',
    'за оказание образовательных услуг по оферте № 16.5 от 06/05/2026'
  ],
  'Рассрочка Нечто 9 сезон 50/50': [
    'за оказание образовательных услуг по оферте №24.1 от 19/12/2025',
    'за оказание образовательных услуг по оферте №24.2 от 13/01/2026'
  ],
  'Рассрочка Нечто 9 сезон 40/30/30': [
    'за оказание образовательных услуг по оферте №25.2 от 13/01/2026',
    'за оказание образовательных услуг по оферте №14/01/25.1'
  ]
}
const productOptions = Object.keys(productTitleMap).map((productName) => ({
  label: productName,
  value: productName
}))
const filters = ref({
  dealId: '',
  dateFrom: null,
  dateTo: null,
  transactionType: [],
  orderStatus: [],
  products: Object.keys(productTitleMap)
})

const metrics = ref({
  sum_price_amount: 0,
  sum_payment_amount: 0,
  sum_payment_system_commission_amount: 0,
  avg_check: 0,
  sum_writeoff_amount: 0,
  sum_writeoff_future_remainder: 0,
  sum_schedule_current_month: 0,
  sum_schedule_next_month: 0,
  sum_schedule_month_after_next: 0,
})
const kpiCards = computed(() => [
  { label: 'Общая выручка', value: formatMoney(metrics.value.sum_price_amount) },
  { label: 'Оплачено заказов на сумму', value: formatMoney(metrics.value.sum_payment_amount) },
  { label: 'Комиссия платежей', value: formatMoney(metrics.value.sum_payment_system_commission_amount) },
  { label: 'Средний чек', value: formatMoney(metrics.value.avg_check) },
  { label: 'Сумма списания выручки по оферте', value: formatMoney(metrics.value.sum_writeoff_amount) },
  {
    label: 'Остаток суммы для списания в будущих периодах',
    value: formatMoney(metrics.value.sum_writeoff_future_remainder),
  },
  {
    label: 'Текущий месяц',
    value: formatMoney(metrics.value.sum_schedule_current_month),
  },
  {
    label: '2-й следующий месяц',
    value: formatMoney(metrics.value.sum_schedule_next_month),
  },
  {
    label: '3-й следующий месяц',
    value: formatMoney(metrics.value.sum_schedule_month_after_next),
  },
])

function formatMoney(value) {
  const num = Number(value || 0)
  return new Intl.NumberFormat('ru-RU', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  }).format(num)
}

function cell(value) {
  if (value === null || value === undefined || value === '') return '—'
  const asNumber = Number(value)
  if (Number.isFinite(asNumber)) {
    const rounded = Math.round(asNumber * 100) / 100
    return new Intl.NumberFormat('ru-RU', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(rounded)
  }
  return String(value)
}

function cellRaw(value) {
  if (value === null || value === undefined || value === '') return '—'
  return String(value)
}

function toNumber(value) {
  const x = Number(value)
  return Number.isFinite(x) ? x : 0
}

function periodShareAmount(priceAmountRaw, index) {
  const pct = PERIOD_PERCENTS[index]
  if (pct === undefined) return ''
  return toNumber(priceAmountRaw) * (pct / 100)
}

function clampPercent(n) {
  const x = toNumber(n)
  if (!Number.isFinite(x)) return 0
  return Math.min(100, Math.max(0, x))
}

/** Гистограмма % списания: 0% — красный (0°), 50% — жёлтый (60°), 100% — зелёный (120°). */
function histogramHue(percent) {
  const p = clampPercent(percent)
  if (p <= 50) return Math.round((p / 50) * 60)
  return Math.round(60 + ((p - 50) / 50) * 60)
}

function histogramBarStyle(percent) {
  const p = clampPercent(percent)
  const w = p <= 0 ? 0 : Math.max(p, 3)
  const hue = histogramHue(p)
  return {
    width: `${w}%`,
    minWidth: p > 0 ? '2px' : '0',
    backgroundColor: `hsl(${hue}deg 72% 45%)`
  }
}

function formatDateForApi(value) {
  if (!value) return null
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return null
  const yyyy = date.getFullYear()
  const mm = String(date.getMonth() + 1).padStart(2, '0')
  const dd = String(date.getDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

function formatDateOnly(value) {
  if (!value) return '—'
  const raw = String(value).trim()
  const fromIso = raw.match(/^(\d{4}-\d{2}-\d{2})/)
  if (fromIso) return fromIso[1]
  const date = new Date(raw)
  if (Number.isNaN(date.getTime())) return cell(value)
  const yyyy = date.getFullYear()
  const mm = String(date.getMonth() + 1).padStart(2, '0')
  const dd = String(date.getDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

function formatDateTime(value) {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return cell(value)
  const dd = String(date.getDate()).padStart(2, '0')
  const mm = String(date.getMonth() + 1).padStart(2, '0')
  const yyyy = date.getFullYear()
  const hh = String(date.getHours()).padStart(2, '0')
  const min = String(date.getMinutes()).padStart(2, '0')
  return `${dd}.${mm}.${yyyy} ${hh}:${min}`
}

function buildParams() {
  const params = {
    limit: pageSize.value,
    offset: (page.value - 1) * pageSize.value,
    sort_field: sortField.value,
    sort_order: sortOrder.value === 1 ? 'asc' : 'desc'
  }
  const dateFrom = formatDateForApi(filters.value.dateFrom)
  const dateTo = formatDateForApi(filters.value.dateTo)
  if (filters.value.dealId && String(filters.value.dealId).trim()) {
    params.deal_id = String(filters.value.dealId).trim()
  }
  if (dateFrom) params.date_from = dateFrom
  if (dateTo) params.date_to = dateTo
  if (Array.isArray(filters.value.transactionType) && filters.value.transactionType.length) {
    const sourceTypes = filters.value.transactionType.flatMap((targetType) => paymentTypeTargetToSources[targetType] || [targetType])
    const uniqueSourceTypes = [...new Set(sourceTypes)]
    if (uniqueSourceTypes.length) {
      params.transaction_type = uniqueSourceTypes.join(',')
    }
  }
  if (Array.isArray(filters.value.products) && filters.value.products.length) {
    const titles = filters.value.products.flatMap((productName) => productTitleMap[productName] || [])
    const uniqueTitles = [...new Set(titles)]
    if (uniqueTitles.length) {
      params.title = uniqueTitles.join(',')
    }
  }
  if (Array.isArray(filters.value.orderStatus) && filters.value.orderStatus.length) {
    params.status = filters.value.orderStatus.join(',')
  }
  return params
}

async function loadFilterOptions() {
  try {
    const { data } = await axios.get(`${apiBase}/api/analytics/getcourse-payments/filters`)
    const rawTypes = data?.transaction_types || []
    const targetTypeSet = new Set(
      rawTypes.map((rawType) => mapPaymentType(rawType)).filter(Boolean)
    )
    transactionTypeOptions.value = [...targetTypeSet].sort((a, b) => a.localeCompare(b, 'ru'))
    statusOptions.value = [...(data?.statuses || [])].sort((a, b) => String(a).localeCompare(String(b), 'ru'))
  } catch (error) {
    transactionTypeOptions.value = []
    statusOptions.value = []
  }
}

async function loadMetrics() {
  metricsLoading.value = true
  try {
    const { data } = await axios.get(`${apiBase}/api/analytics/getcourse-payments/metrics`, {
      params: buildParams()
    })
    metrics.value = {
      sum_price_amount: Number(data?.sum_price_amount || 0),
      sum_payment_amount: Number(data?.sum_payment_amount || 0),
      sum_payment_system_commission_amount: Number(data?.sum_payment_system_commission_amount || 0),
      avg_check: Number(data?.avg_check || 0),
      sum_writeoff_amount: Number(data?.sum_writeoff_amount || 0),
      sum_writeoff_future_remainder: Number(data?.sum_writeoff_future_remainder || 0),
      sum_schedule_current_month: Number(data?.sum_schedule_current_month || 0),
      sum_schedule_next_month: Number(data?.sum_schedule_next_month || 0),
      sum_schedule_month_after_next: Number(data?.sum_schedule_month_after_next || 0),
    }
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: 'Не удалось загрузить KPI',
      life: 4000
    })
  } finally {
    metricsLoading.value = false
  }
}

async function loadRows() {
  loading.value = true
  try {
    const { data } = await axios.get(`${apiBase}/api/analytics/getcourse-payments/rows`, {
      params: buildParams()
    })
    rows.value = (data?.data || []).map((row) => {
      const productType = mapProductType(row.title)
      const paymentAt = String(row.payment_at || '').trim()
      const backendWriteoffAmount = row.writeoff_amount
      const offerWriteoffAmount = backendWriteoffAmount === null || backendWriteoffAmount === undefined || backendWriteoffAmount === ''
        ? calculateOfferWriteoff(productType, row.price_amount, paymentAt)
        : backendWriteoffAmount
      const backendOrderEndAt = row.order_end_at
      const orderEndAt = backendOrderEndAt === null || backendOrderEndAt === undefined || backendOrderEndAt === ''
        ? calculateOrderEndAt(productType, row.payment_amount, paymentAt)
        : backendOrderEndAt
      const backendDaysRemaining = row.days_remaining
      const daysRemaining = backendDaysRemaining === null || backendDaysRemaining === undefined || backendDaysRemaining === ''
        ? calculateDaysRemaining(orderEndAt)
        : backendDaysRemaining
      const futureWriteoffRemainder =
        row.writeoff_future_remainder === null || row.writeoff_future_remainder === undefined || row.writeoff_future_remainder === ''
          ? (offerWriteoffAmount === ''
            ? ''
            : Math.round((toNumber(row.price_amount) - toNumber(offerWriteoffAmount)) * 100) / 100)
          : row.writeoff_future_remainder
      const orderPaymentPercent =
        row.order_payment_percent === null || row.order_payment_percent === undefined || row.order_payment_percent === ''
          ? getOrderPaymentPercent(row)
          : row.order_payment_percent
      const preparedRow = {
        ...row,
        writeoff_amount: offerWriteoffAmount,
        writeoff_future_remainder: futureWriteoffRemainder,
        order_payment_percent: orderPaymentPercent,
        order_created_at: row.created_at,
        order_end_at: orderEndAt,
        days_remaining: daysRemaining,
        order_status: row.status,
        internal_status: null,
        order_id: row.deal_id,
        order_number: row.deal_number,
        product_type: productType,
        order_composition: row.title,
        order_cost: row.price_amount,
        paid_in_order: row.payment_amount,
        ps_commission_order: row.payment_system_commission_amount,
        earned: row.earned_amount,
        partner_id: row.order_partner_id,
        partner_name: row.order_partner_full_name,
        partner_email_gc: row.order_partner_email,
        order_gc_link: row.getcourse_deal_link,
        payment_date: null,
        payment_type: mapPaymentType(row.payment_system),
        payment_status: null,
        commission_amount: row.payment_system_commission_amount,
        operation_proof: null,
        user_full_name: row.user_name,
        phone: row.user_phone,
        city: null,
        country: null,
        participant_type: null,
        niche: null,
        grade_point_a: null,
        employee_count: null
      }
      return {
        ...preparedRow,
        revenue_writeoff_percent:
          row.revenue_writeoff_percent === null || row.revenue_writeoff_percent === undefined || row.revenue_writeoff_percent === ''
            ? getRevenueWriteoffPercent(preparedRow)
            : row.revenue_writeoff_percent
      }
    })
    total.value = data?.meta?.total || 0
    expandedRows.value = {}
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: 'Не удалось загрузить таблицу',
      life: 4000
    })
  } finally {
    loading.value = false
  }
}

function getDealNumber(rowData) {
  return String(rowData?.deal_number || '').trim()
}

function getDealPaymentsRows(rowData) {
  const dealNumber = getDealNumber(rowData)
  return dealPaymentsByDealNumber.value[dealNumber] || []
}

function getDealPaymentsLoading(rowData) {
  const dealNumber = getDealNumber(rowData)
  return Boolean(dealPaymentsLoadingByDealNumber.value[dealNumber])
}

function getDealPaymentsError(rowData) {
  const dealNumber = getDealNumber(rowData)
  return dealPaymentsErrorByDealNumber.value[dealNumber] || ''
}

async function loadDealPayments(dealNumber) {
  if (!dealNumber) return
  if (dealPaymentsByDealNumber.value[dealNumber]) return
  if (dealPaymentsLoadingByDealNumber.value[dealNumber]) return

  dealPaymentsLoadingByDealNumber.value = {
    ...dealPaymentsLoadingByDealNumber.value,
    [dealNumber]: true
  }
  dealPaymentsErrorByDealNumber.value = {
    ...dealPaymentsErrorByDealNumber.value,
    [dealNumber]: ''
  }

  try {
    const { data } = await axios.get(`${apiBase}/api/analytics/getcourse-payments/deal-payments`, {
      params: { deal_number: dealNumber }
    })

    const mappedRows = (data?.data || []).map((paymentRow) => ({
      ...paymentRow,
      payment_type: mapPaymentType(paymentRow.payment_type)
    }))

    dealPaymentsByDealNumber.value = {
      ...dealPaymentsByDealNumber.value,
      [dealNumber]: mappedRows
    }
  } catch (error) {
    dealPaymentsErrorByDealNumber.value = {
      ...dealPaymentsErrorByDealNumber.value,
      [dealNumber]: 'Не удалось загрузить платежи по заказу'
    }
  } finally {
    dealPaymentsLoadingByDealNumber.value = {
      ...dealPaymentsLoadingByDealNumber.value,
      [dealNumber]: false
    }
  }
}

function onRowExpand(event) {
  const dealNumber = getDealNumber(event?.data)
  loadDealPayments(dealNumber)
}

function onRowCollapse() {}

async function refreshData() {
  await Promise.all([loadMetrics(), loadRows()])
}

async function onExportCsv() {
  exportLoading.value = true
  try {
    const response = await axios.get(`${apiBase}/api/analytics/getcourse-payments/export.csv`, {
      params: buildParams(),
      responseType: 'blob'
    })

    const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    const fileName = `deals-dashboard-${new Date().toISOString().slice(0, 10)}.csv`
    link.href = url
    link.setAttribute('download', fileName)
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: 'Не удалось скачать CSV',
      life: 4000
    })
  } finally {
    exportLoading.value = false
  }
}

function getRevenueWriteoffPercent(data) {
  if (data.writeoff_amount === '' || data.writeoff_amount === null || data.writeoff_amount === undefined) return ''
  const priceAmount = toNumber(data.price_amount)
  if (priceAmount <= 0) return ''
  return ((toNumber(data.writeoff_amount) / priceAmount) * 100).toFixed(2)
}

function getOrderPaymentPercent(data) {
  const cost = toNumber(data.price_amount)
  if (cost <= 0) return 0
  return ((toNumber(data.payment_amount) / cost) * 100).toFixed(2)
}

function mapPaymentType(rawValue) {
  const value = String(rawValue || '').trim()
  if (!value) return '—'
  return paymentTypeSourceToTarget[value] || value
}

function mapProductType(rawTitle) {
  const title = String(rawTitle || '').trim()
  if (!title) return '—'
  const productTypeMap = {
    'за оказание образовательных услуг по оферте №14.1 от 17/10/2025': '[9 сезон] продление из 8 в 9 сезон',
    'за оказание образовательных услуг по оферте №14.2 от 13/01/2026': '[9 сезон] продление из 8 в 9 сезон',
    'за оказание образовательных услуг по оферте №16.1 от 21/10/2025': '[9 сезон] новый участник',
    'за оказание образовательных услуг по оферте №16.3 от 26/02/2026': '[9 сезон] новый участник',
    'за оказание образовательных услуг по оферте №16.4 от 06/03/2026': '[9 сезон] новый участник',
    'за оказание образовательных услуг по оферте №24.1 от 19/12/2025': 'Рассрочка Нечто 9 сезон 50/50',
    'за оказание образовательных услуг по оферте №16.2 от 13/01/2026': '[9 сезон] новый участник',
    'за оказание образовательных услуг по оферте №24.2 от 13/01/2026': 'Рассрочка Нечто 9 сезон 50/50',
    'за оказание образовательных услуг по оферте №25.2 от 13/01/2026': 'Рассрочка Нечто 9 сезон 40/30/30',
    'за оказание образовательных услуг по оферте №14/01/25.1': 'Рассрочка Нечто 9 сезон 40/30/30',
    'за оказание образовательных услуг по оферте №14.3 от 05/03/2026': '[9 сезон] продление из 8 в 9 сезон',
    'за оказание образовательных услуг по оферте №14.4 от 01/04/2026': '[9 сезон] продление из 8 в 9 сезон',
    'за оказание образовательных услуг по оферте № 16.5 от 06/05/2026': '[9 сезон] новый участник'
  }
  return productTypeMap[title] || '—'
}

function calculateOfferWriteoff(productType, priceAmountRaw, paymentAtRaw) {
  const allProductTypes = Object.keys(productTitleMap)
  const isInList = allProductTypes.includes(productType)
  const priceAmount = toNumber(priceAmountRaw)
  if (!isInList || priceAmount <= 40000) return ''

  const paymentDate = new Date(paymentAtRaw)
  if (Number.isNaN(paymentDate.getTime())) return ''

  const daysDiff = (Date.now() - paymentDate.getTime()) / (1000 * 60 * 60 * 24)
  const months = Math.ceil(daysDiff / 30)
  const result = priceAmount * 0.77 * Math.pow(0.93, months)
  return Math.round(result * 100) / 100
}

function calculateOrderEndAt(productType, paidInOrderRaw, paymentAtRaw) {
  const allProductTypes = Object.keys(productTitleMap)
  const isInList = allProductTypes.includes(productType)
  const paidInOrder = toNumber(paidInOrderRaw)
  if (!isInList || paidInOrder <= 40000) return ''

  const raw = String(paymentAtRaw || '').trim()
  const datePart = raw.match(/^(\d{4})-(\d{2})-(\d{2})/)
  const paymentDate = datePart
    ? new Date(Number(datePart[1]), Number(datePart[2]) - 1, Number(datePart[3]))
    : new Date(paymentAtRaw)
  if (Number.isNaN(paymentDate.getTime())) return ''

  paymentDate.setDate(paymentDate.getDate() + 365)
  const yyyy = paymentDate.getFullYear()
  const mm = String(paymentDate.getMonth() + 1).padStart(2, '0')
  const dd = String(paymentDate.getDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

function calculateDaysRemaining(orderEndAtRaw) {
  if (!orderEndAtRaw) return ''
  const endDate = new Date(orderEndAtRaw)
  if (Number.isNaN(endDate.getTime())) return ''
  const diffDays = (endDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24)
  return Math.max(0, Math.ceil(diffDays))
}

function onPage(event) {
  page.value = event.page + 1
  pageSize.value = event.rows
  loadRows()
}

function onSort(event) {
  sortField.value = event.sortField || 'created_at'
  sortOrder.value = event.sortOrder || -1
  loadRows()
}

/*function getDealLink(data) {
  return data?.getcourse_deal_link || (data?.deal_id ? `https://get-nechto.ru/sales/control/deal/update/id/${data.deal_id}` : '')
}

function openDealLink(data) {
  const link = getDealLink(data)
  if (link) window.open(link, '_blank', 'noopener,noreferrer')
}*/

function resetFilters() {
  filters.value = {
    dealId: '',
    dateFrom: null,
    dateTo: null,
    transactionType: [],
    orderStatus: [],
    products: Object.keys(productTitleMap)
  }
  page.value = 1
  refreshData()
}

if (SHOW_ORDERS_TAB) {
  watch(
    () => [
      filters.value.dealId,
      filters.value.dateFrom,
      filters.value.dateTo,
      filters.value.transactionType,
      filters.value.orderStatus,
      filters.value.products
    ],
    () => {
      page.value = 1
      refreshData()
    }
  )
}

onMounted(async () => {
  await userStore.checkAuth()
  if (!SHOW_ORDERS_TAB) return
  await loadFilterOptions()
  await refreshData()
})
</script>

<template>
  <ToastVue />

  <div class="dashboard-page">
    <header class="dashboard-header">
      <div>
        <h1>Dashboard платежей</h1>
        <p>Аналитика GetCourse-платежей для оперативного контроля финансов.</p>
      </div>
    </header>

    <Tabs v-model:value="activeDashboardTab" class="dashboard-tabs">
      <TabList v-if="SHOW_ORDERS_TAB">
        <Tab value="orders">Заказы</Tab>
        <Tab value="summary">Сводка по оплатам</Tab>
      </TabList>

      <TabPanels class="dashboard-tab-panels">
        <TabPanel value="orders">
          <div class="dashboard-tab-content">
    <section class="kpi-grid">
      <article v-for="card in kpiCards" :key="card.label" class="kpi-card">
        <div class="kpi-label">{{ card.label }}</div>
        <div class="kpi-value">{{ card.value }}</div>
      </article>
    </section>

    <section class="filters-card">
      <div class="filter-field">
        <label>ID заказа</label>
        <InputText
          v-model="filters.dealId"
          placeholder="Поиск по deal_id"
        />
      </div>

      <div class="filter-field">
        <label>Дата с</label>
        <DatePicker
          v-model="filters.dateFrom"
          dateFormat="dd.mm.yy"
          showIcon
          fluid
          appendTo="self"
          placeholder="Начало периода"
        />
      </div>

      <div class="filter-field">
        <label>Дата по</label>
        <DatePicker
          v-model="filters.dateTo"
          dateFormat="dd.mm.yy"
          showIcon
          fluid
          appendTo="self"
          placeholder="Конец периода"
        />
      </div>

      <div class="filter-field">
        <label>Тип оплаты</label>
        <MultiSelect
          v-model="filters.transactionType"
          :options="transactionTypeOptions"
          display="chip"
          :maxSelectedLabels="2"
          appendTo="self"
          placeholder="Все типы"
        />
      </div>

      <div class="filter-field">
        <label>Статус</label>
        <MultiSelect
          v-model="filters.orderStatus"
          :options="statusOptions"
          display="chip"
          :maxSelectedLabels="2"
          appendTo="self"
          placeholder="Все статусы"
        />
      </div>

      <div class="filter-field grow">
        <label>Продукт</label>
        <MultiSelect
          v-model="filters.products"
          :options="productOptions"
          optionLabel="label"
          optionValue="value"
          display="chip"
          :maxSelectedLabels="2"
          appendTo="self"
          placeholder="Все продукты"
        />
      </div>

      <div class="filter-actions">
        <ButtonVue type="button" label="Сброс" severity="secondary" text @click="resetFilters" />
        <ButtonVue type="button" label="Скачать CSV" icon="pi pi-download" :loading="exportLoading" @click="onExportCsv" />
      </div>
    </section>

    <section class="table-card table-card--scroll">
      <DataTable
        :value="rows"
        v-model:expandedRows="expandedRows"
        :lazy="true"
        :loading="loading"
        :paginator="true"
        :rows="pageSize"
        :rowsPerPageOptions="[100, 250, 500]"
        :first="(page - 1) * pageSize"
        :totalRecords="total"
        :sortField="sortField"
        :sortOrder="sortOrder"
        dataKey="deal_id"
        class="payments-table"
        scrollable
        scrollHeight="560px"
        @page="onPage"
        @sort="onSort"
        @rowExpand="onRowExpand"
        @rowCollapse="onRowCollapse"
      >
        <ColumnTable expander :style="{ width: '44px' }" />

        <ColumnTable field="revenue_writeoff_histogram" :style="{ width: '88px' }">
          <template #header>
            <span class="col-head col-head--narrow" title="Гистограмма % списания выручки">Гист.</span>
          </template>
          <template #body="{ data }">
            <div class="mini-bar-track" title="Гистограмма % списания выручки">
              <div class="mini-bar-fill" :style="histogramBarStyle(data.revenue_writeoff_percent)" />
            </div>
          </template>
        </ColumnTable>

        <ColumnTable field="revenue_writeoff_percent" :style="{ minWidth: '96px' }">
          <template #header>
            <span class="col-head">% списания<br />выручки</span>
          </template>
          <template #body="{ data }">{{ `${cell(data.revenue_writeoff_percent)}%` }}</template>
        </ColumnTable>

        <ColumnTable field="writeoff_amount" :style="{ minWidth: '110px' }">
          <template #header>Сумма списания выручки по оферте</template>
          <template #body="{ data }">{{ cell(data.writeoff_amount) }}</template>
        </ColumnTable>

        <ColumnTable field="writeoff_future_remainder" :style="{ minWidth: '130px' }">
          <template #header>Остаток суммы для списания в будущих периодах</template>
          <template #body="{ data }">{{ cell(data.writeoff_future_remainder) }}</template>
        </ColumnTable>

        <ColumnTable
          field="period_columns_toggle"
          :style="{ width: '2rem', minWidth: '2rem', maxWidth: '2.25rem' }"
        >
          <template #header>
            <div class="period-toggle-head">
              <ButtonVue
                type="button"
                rounded
                text
                severity="secondary"
                size="small"
                class="period-toggle-btn"
                :icon="showPeriodColumns ? 'pi pi-minus' : 'pi pi-plus'"
                :aria-label="showPeriodColumns ? 'Скрыть столбцы графика 0–12' : 'Показать столбцы графика 0–12'"
                @click.stop="showPeriodColumns = !showPeriodColumns"
              />
            </div>
          </template>
          <template #body><span class="period-toggle-cell" /></template>
        </ColumnTable>

        <template v-for="(pct, idx) in PERIOD_PERCENTS" :key="`period-col-${idx}`">
          <ColumnTable v-if="showPeriodColumns" :field="`period_${idx}`" :style="{ minWidth: '76px' }">
            <template #header>
              <span class="col-head">{{ idx }}<br />{{ pct }}%</span>
            </template>
            <template #body="{ data }">{{ cell(data[`period_${idx}_amount`] ?? periodShareAmount(data.price_amount, idx)) }}</template>
          </ColumnTable>
        </template>

        <ColumnTable field="order_payment_percent" :style="{ minWidth: '120px' }">
          <template #header>
            <span class="col-head">% оплаты заказа</span>
          </template>
          <template #body="{ data }">
            <span class="col-sub" :title="'Оплачено в заказе / Стоимость заказа'">{{ `${cell(data.order_payment_percent)}%` }}</span>
          </template>
        </ColumnTable>

        <ColumnTable field="order_created_at" sortField="created_at" sortable :style="{ minWidth: '130px' }">
          <template #header>Дата создания заказа</template>
          <template #body="{ data }">{{ formatDateOnly(data.order_created_at) }}</template>
        </ColumnTable>

        <ColumnTable field="order_end_at" :style="{ minWidth: '120px' }">
          <template #header>Дата завершения</template>
          <template #body="{ data }">{{ formatDateOnly(data.order_end_at) }}</template>
        </ColumnTable>

        <ColumnTable field="days_remaining" :style="{ minWidth: '100px' }">
          <template #header>Остаток в днях</template>
          <template #body="{ data }">{{ cell(data.days_remaining) }}</template>
        </ColumnTable>

        <ColumnTable field="order_status" sortField="status" sortable :style="{ minWidth: '140px' }">
          <template #header>
            <span class="col-head">Статус</span>
          </template>
          <template #body="{ data }">
            <span class="col-sub" :title="'Завершён / Частично оплачен / Ожидает возврата / В работе'">{{ cell(data.order_status) }}</span>
          </template>
        </ColumnTable>

        <ColumnTable field="internal_status" :style="{ minWidth: '130px' }">
          <template #header>
            <span class="col-head">Внутренний статус</span>
          </template>
          <template #body="{ data }">
            <span class="col-sub" :title="'Лист «Справочник»'">{{ cell(data.internal_status) }}</span>
          </template>
        </ColumnTable>

        <ColumnTable field="order_id" sortField="deal_id" sortable :style="{ minWidth: '96px' }">
          <template #header>ID заказа</template>
          <template #body="{ data }">{{ cellRaw(data.order_id) }}</template>
        </ColumnTable>

        <ColumnTable field="order_number" sortField="deal_number" sortable :style="{ minWidth: '110px' }">
          <template #header>Номер заказа</template>
          <template #body="{ data }">{{ cellRaw(data.order_number) }}</template>
        </ColumnTable>

        <ColumnTable field="product_type" :style="{ minWidth: '120px' }">
          <template #header>
            <span class="col-head">Вид продукта</span>
          </template>
          <template #body="{ data }">
            <span class="col-sub" :title="'Лист «Справочник», столбец F'">{{ cell(data.product_type) }}</span>
          </template>
        </ColumnTable>

        <ColumnTable field="order_composition" :style="{ minWidth: '140px' }">
          <template #header>Состав заказа</template>
          <template #body="{ data }">{{ cell(data.order_composition) }}</template>
        </ColumnTable>

        <ColumnTable field="order_cost" sortField="price_amount" sortable :style="{ minWidth: '120px' }">
          <template #header>Стоимость заказа</template>
          <template #body="{ data }">{{ cell(data.order_cost) }}</template>
        </ColumnTable>

        <ColumnTable field="paid_in_order" sortField="payment_amount" sortable :style="{ minWidth: '130px' }">
          <template #header>
            <span class="col-head">Оплачено в заказе</span>
          </template>
          <template #body="{ data }">
            <span class="col-sub" :title="'Сумма всех платежей по заказу'">{{ cell(data.paid_in_order) }}</span>
          </template>
        </ColumnTable>

        <ColumnTable
          field="ps_commission_order"
          sortField="payment_system_commission_amount"
          sortable
          :style="{ minWidth: '130px' }"
        >
          <template #header>
            <span class="col-head">Комиссия ПС</span>
          </template>
          <template #body="{ data }">
            <span class="col-sub" :title="'Сумма по всем платежам в заказе'">{{ cell(data.ps_commission_order) }}</span>
          </template>
        </ColumnTable>

        <ColumnTable field="earned" sortField="earned_amount" sortable :style="{ minWidth: '110px' }">
          <template #header>
            <span class="col-head">Заработано</span>
          </template>
          <template #body="{ data }">
            <span class="col-sub" :title="'Оплачено − комиссия ПС'">{{ cell(data.earned) }}</span>
          </template>
        </ColumnTable>

        <ColumnTable field="order_gc_link" :style="{ minWidth: '120px' }">
          <template #header>Ссылка на заказ в GetCourse</template>
          <template #body="{ data }">
            <a
              v-if="data.order_gc_link"
              :href="data.order_gc_link"
              target="_blank"
              rel="noopener noreferrer"
              class="gc-order-link"
            >
              Перейти
            </a>
            <span v-else>—</span>
          </template>
        </ColumnTable>

        <ColumnTable field="user_id" :style="{ minWidth: '100px' }">
          <template #header>ID пользователя</template>
          <template #body="{ data }">{{ cellRaw(data.user_id) }}</template>
        </ColumnTable>

        <ColumnTable field="user_full_name" :style="{ minWidth: '140px' }">
          <template #header>
            <span class="col-head">Пользователь</span>
          </template>
          <template #body="{ data }">
            <span class="col-sub" :title="'Имя и фамилия'">{{ cell(data.user_full_name) }}</span>
          </template>
        </ColumnTable>

        <ColumnTable field="user_email" :style="{ minWidth: '160px' }">
          <template #header>Email</template>
          <template #body="{ data }">{{ cell(data.user_email) }}</template>
        </ColumnTable>

        <ColumnTable field="phone" :style="{ minWidth: '120px' }">
          <template #header>Телефон</template>
          <template #body="{ data }">{{ cellRaw(data.phone) }}</template>
        </ColumnTable>

        <template #empty>
          <div class="empty-hint">Данные из новой таблицы подключатся позже — сейчас только структура колонок.</div>
        </template>

        <template #expansion="{ data }">
          <div class="deal-payments-expansion">
            <div v-if="getDealPaymentsLoading(data)" class="deal-payments-loading">Загрузка платежей...</div>
            <div v-else-if="getDealPaymentsError(data)" class="deal-payments-error">{{ getDealPaymentsError(data) }}</div>
            <div v-else-if="!getDealPaymentsRows(data).length" class="deal-payments-empty">Платежи по заказу не найдены</div>
            <DataTable v-else :value="getDealPaymentsRows(data)" class="deal-payments-table" size="small">
              <ColumnTable field="payment_created_at" header="Дата оплаты">
                <template #body="{ data: paymentData }">{{ formatDateTime(paymentData.payment_created_at) }}</template>
              </ColumnTable>
              <ColumnTable field="payment_type" header="Тип платежа" />
              <ColumnTable field="payment_status" header="Статус по оплате" />
              <ColumnTable field="payment_amount" header="Сумма оплаты">
                <template #body="{ data: paymentData }">{{ cell(paymentData.payment_amount) }}</template>
              </ColumnTable>
              <ColumnTable field="payment_commission_amount" header="Сумма комиссии">
                <template #body="{ data: paymentData }">{{ cell(paymentData.payment_commission_amount) }}</template>
              </ColumnTable>
            </DataTable>
          </div>
        </template>
      </DataTable>
    </section>
          </div>
        </TabPanel>

        <TabPanel value="summary">
          <DashboardPaymentsSummaryTab />
        </TabPanel>
      </TabPanels>
    </Tabs>
  </div>
</template>

<style scoped>
.dashboard-page {
  max-width: 1760px;
  margin: 0 auto;
  padding: 1.25rem 1rem 2rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  min-height: 100vh;
  background: #f8fafc;
  color: #0f172a;
  color-scheme: light;
  --p-surface-0: #ffffff;
  --p-surface-50: #f8fbff;
  --p-surface-100: #eef6ff;
  --p-surface-200: #dbeafe;
  --p-surface-950: #f8fafc;
  --p-content-background: #ffffff;
  --p-text-color: #0f172a;
  --p-text-muted-color: #64748b;
  --p-primary-color: #2563eb;
  --p-primary-contrast-color: #ffffff;
  --p-highlight-background: #eff6ff;
  --p-highlight-color: #1e3a8a;
  --p-form-field-background: #ffffff;
  --p-form-field-color: #0f172a;
  --p-form-field-border-color: #c8dff5;
  --p-overlay-background: #ffffff;
  --p-overlay-color: #0f172a;
}

.dashboard-page :deep(.p-datatable) {
  background: #ffffff;
  color: #0f172a;
}

.dashboard-page :deep(.p-datatable-table-container) {
  border-radius: 12px;
}

.dashboard-page :deep(.p-datatable-thead > tr > th) {
  background: #f0f7ff;
  color: #0b3558;
  border-bottom: 1px solid #cfe3f8;
  font-weight: 700;
  font-size: 0.75rem;
  padding: 0.65rem 0.5rem;
  vertical-align: bottom;
  white-space: normal;
  line-height: 1.25;
}

.dashboard-page :deep(.p-datatable-tbody > tr > td) {
  background: #ffffff;
  color: #0f172a;
  border-bottom: 1px solid #e6eef7;
  font-size: 0.8rem;
  white-space: nowrap;
  padding: 0.5rem 0.5rem;
}

.dashboard-page :deep(.p-datatable-tbody > tr:hover > td) {
  background: #f8fcff;
}

.dashboard-page :deep(.p-datatable-mask),
.dashboard-page :deep(.p-datatable-loading-icon) {
  background: rgba(255, 255, 255, 0.72);
  color: #2563eb;
}

.dashboard-page :deep(.p-paginator) {
  background: #ffffff;
  border-top: 1px solid #e6eef7;
  color: #0f172a;
}

.dashboard-page :deep(.p-paginator .p-paginator-page),
.dashboard-page :deep(.p-paginator .p-paginator-first),
.dashboard-page :deep(.p-paginator .p-paginator-prev),
.dashboard-page :deep(.p-paginator .p-paginator-next),
.dashboard-page :deep(.p-paginator .p-paginator-last),
.dashboard-page :deep(.p-paginator .p-select) {
  background: #ffffff;
  color: #0f172a;
  border-color: #dbe2ea;
}

.dashboard-page :deep(.p-paginator .p-paginator-page.p-paginator-page-selected) {
  background: #eff6ff;
  color: #1e3a8a;
  border-color: #93c5fd;
}

.dashboard-page :deep(.p-inputtext),
.dashboard-page :deep(.p-select),
.dashboard-page :deep(.p-select-label),
.dashboard-page :deep(.p-multiselect),
.dashboard-page :deep(.p-multiselect-label),
.dashboard-page :deep(.p-datepicker-input),
.dashboard-page :deep(.p-datepicker-dropdown) {
  background: #ffffff;
  border: 1px solid #c8dff5;
  color: #0f172a;
  box-shadow: none;
}

.dashboard-page :deep(.p-inputtext:enabled:focus),
.dashboard-page :deep(.p-select:not(.p-disabled).p-focus),
.dashboard-page :deep(.p-datepicker-input:enabled:focus) {
  border-color: #3b82f6;
  box-shadow: 0 0 0 0.18rem rgba(59, 130, 246, 0.2);
}

.dashboard-page :deep(.p-button) {
  border-radius: 10px;
}

.dashboard-page :deep(.p-button:not(.p-button-text):not(.p-button-outlined)) {
  background: linear-gradient(135deg, #1d9bf0 0%, #2563eb 100%);
  border-color: #2563eb;
}

.dashboard-page :deep(.p-button.p-button-secondary) {
  background: #eef6ff;
  color: #1e3a8a;
  border-color: #c7dcf7;
}

.col-head {
  display: inline-block;
  max-width: 11rem;
}

.col-head--narrow {
  max-width: 4.5rem;
}

.col-sub {
  display: inline-block;
  max-width: 10rem;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: bottom;
}

.mini-bar-track {
  width: 100%;
  max-width: 72px;
  height: 8px;
  border-radius: 999px;
  background: #e2e8f0;
  overflow: hidden;
}

.mini-bar-fill {
  height: 100%;
  border-radius: 999px;
  transition: width 0.2s ease, background 0.2s ease;
}

.empty-hint {
  padding: 1.25rem 1rem;
  text-align: center;
  color: #64748b;
  font-size: 0.9rem;
}

.deal-payments-expansion {
  padding: 0.5rem 0.25rem 0.75rem;
}

.deal-payments-loading,
.deal-payments-error,
.deal-payments-empty {
  font-size: 0.9rem;
  color: #475569;
  padding: 0.5rem 0.25rem 0.75rem;
}

.deal-payments-error {
  color: #b91c1c;
}

.deal-payments-table {
  display: inline-block;
  width: fit-content;
  max-width: 100%;
}

.deal-payments-table :deep(.p-datatable-table) {
  width: auto;
}

.deal-payments-table :deep(.p-datatable-thead > tr > th),
.deal-payments-table :deep(.p-datatable-tbody > tr > td) {
  white-space: nowrap;
  padding: 0.3rem 0.45rem;
  font-size: 0.82rem;
}

.gc-order-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.22rem 0.55rem;
  border-radius: 8px;
  border: 1px solid #bfdbfe;
  background: #eff6ff;
  color: #1d4ed8;
  font-size: 0.8rem;
  text-decoration: none;
  transition: background-color 0.15s ease, border-color 0.15s ease;
}

.gc-order-link:hover {
  background: #dbeafe;
  border-color: #93c5fd;
}

.dashboard-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}

.dashboard-header h1 {
  margin: 0;
  font-size: 2rem;
  font-weight: 700;
}

.dashboard-header p {
  margin: 0.35rem 0 0;
  font-size: 1rem;
  color: #64748b;
}

.dashboard-tabs {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.dashboard-tabs :deep(.p-tablist) {
  background: #ffffff;
  border: 1px solid #dbe2ea;
  border-radius: 12px;
  padding: 0.35rem 0.5rem 0;
}

.dashboard-tabs :deep(.p-tablist-tab-list) {
  gap: 0.25rem;
}

.dashboard-tabs :deep(.p-tab) {
  color: #64748b;
  background: transparent;
  border-color: transparent;
  font-weight: 600;
  padding: 0.7rem 1.1rem;
  margin-bottom: -1px;
}

.dashboard-tabs :deep(.p-tab:hover) {
  color: #1e3a8a;
  background: #f8fbff;
}

.dashboard-tabs :deep(.p-tab.p-tab-active) {
  color: #1e3a8a;
  background: #ffffff;
  border-color: #2563eb;
}

.dashboard-tabs :deep(.p-tabpanels) {
  background: transparent;
  padding: 0;
}

.dashboard-tab-panels {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.dashboard-tab-content {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.kpi-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 0.75rem;
}

.kpi-card {
  border: 1px solid #dbe2ea;
  border-radius: 14px;
  padding: 0.9rem 1rem;
  background: #ffffff;
  min-height: 96px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 0.4rem;
}

.kpi-label {
  font-size: 0.85rem;
  color: #64748b;
}

.kpi-value {
  font-size: 1.6rem;
  font-weight: 700;
  line-height: 1.1;
}

.filters-card {
  border: 1px solid #dbe2ea;
  border-radius: 14px;
  padding: 0.85rem;
  background: #ffffff;
  display: grid;
  grid-template-columns: repeat(12, minmax(0, 1fr));
  gap: 0.75rem;
  align-items: end;
}

.filter-field {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  grid-column: span 2;
}

.filter-field.grow {
  grid-column: span 4;
}

.filter-field label {
  font-size: 0.8rem;
  color: #64748b;
}

.filter-actions {
  grid-column: span 2;
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
}

.table-card {
  border: 1px solid #dbe2ea;
  border-radius: 14px;
  padding: 0.25rem;
  background: #ffffff;
}

.table-card--scroll {
  overflow-x: auto;
}

.period-toggle-head {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  min-height: 2rem;
}

.period-toggle-btn {
  width: 1.5rem !important;
  min-width: 1.5rem !important;
  height: 1.5rem !important;
  padding: 0 !important;
}

.period-toggle-btn :deep(.p-button-icon) {
  font-size: 0.65rem;
}

.period-toggle-cell {
  display: block;
  min-height: 0.5rem;
}

.payments-table :deep(.p-datatable-header-cell) {
  font-size: 0.75rem;
}

.payments-table :deep(.p-datatable-tbody > tr > td) {
  font-size: 0.8rem;
}

@media (max-width: 1480px) {
  .kpi-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 1200px) {
  .filters-card {
    grid-template-columns: repeat(6, minmax(0, 1fr));
  }

  .filter-field,
  .filter-field.grow,
  .filter-actions {
    grid-column: span 3;
  }
}
</style>

<style>
body.dashboard-payments-light {
  background: #f8fafc !important;
  color: #0f172a !important;
  color-scheme: light !important;
}

body.dashboard-payments-light .p-datepicker-panel,
body.dashboard-payments-light .p-multiselect-overlay,
body.dashboard-payments-light .p-multiselect-list,
body.dashboard-payments-light .p-select-overlay,
body.dashboard-payments-light .p-select-list,
body.dashboard-payments-light .p-toast,
body.dashboard-payments-light .p-toast-message,
body.dashboard-payments-light .p-tablist,
body.dashboard-payments-light .p-tabpanels {
  background: #ffffff !important;
  color: #0f172a !important;
  border-color: #dbeafe !important;
}

body.dashboard-payments-light .p-datepicker-panel .p-datepicker-header,
body.dashboard-payments-light .p-datepicker-panel .p-datepicker-title,
body.dashboard-payments-light .p-datepicker-panel .p-datepicker-weekday {
  background: #ffffff !important;
  color: #0f172a !important;
}

body.dashboard-payments-light .p-datepicker-panel .p-datepicker-day,
body.dashboard-payments-light .p-multiselect-option,
body.dashboard-payments-light .p-select-option,
body.dashboard-payments-light .p-tab,
body.dashboard-payments-light .p-paginator .p-paginator-page,
body.dashboard-payments-light .p-toast-message-content,
body.dashboard-payments-light .p-toast-summary,
body.dashboard-payments-light .p-toast-detail {
  color: #0f172a !important;
}

body.dashboard-payments-light .p-datepicker-panel .p-datepicker-day:hover,
body.dashboard-payments-light .p-multiselect-option:hover,
body.dashboard-payments-light .p-select-option:hover,
body.dashboard-payments-light .p-tab:hover {
  background: #eff6ff !important;
  color: #1e3a8a !important;
}

body.dashboard-payments-light .p-tab.p-tab-active {
  background: #ffffff !important;
  color: #1e3a8a !important;
  border-color: #2563eb !important;
}

body.dashboard-payments-light .p-multiselect-chip,
body.dashboard-payments-light .p-chip {
  background: #dbeafe !important;
  color: #1e3a8a !important;
}

body.dashboard-payments-light .p-inputtext,
body.dashboard-payments-light .p-select,
body.dashboard-payments-light .p-select-label,
body.dashboard-payments-light .p-multiselect,
body.dashboard-payments-light .p-multiselect-label,
body.dashboard-payments-light .p-datepicker-input,
body.dashboard-payments-light .p-paginator,
body.dashboard-payments-light .p-datatable,
body.dashboard-payments-light .p-datatable-thead > tr > th,
body.dashboard-payments-light .p-datatable-tbody > tr > td {
  background: #ffffff !important;
  color: #0f172a !important;
  border-color: #dbe2ea !important;
}

body.dashboard-payments-light .p-icon,
body.dashboard-payments-light .p-button-icon,
body.dashboard-payments-light .p-select-dropdown,
body.dashboard-payments-light .p-multiselect-dropdown {
  color: #64748b !important;
}
</style>
